/**
 * Wall-clock helpers that work in the TENANT's timezone.
 *
 * WHY THIS EXISTS
 *
 * `<input type="datetime-local">` and the DateTimePicker built on it deal in
 * bare wall-clock strings — "2026-09-02T15:40" — with no zone attached. Two
 * conversions therefore have to agree, and in the PTW form they did not:
 *
 *   - the default was produced with `new Date().toISOString().slice(0,16)`,
 *     which is the wall clock in UTC;
 *   - the submit read it back with `new Date(value)`, and a datetime-local
 *     string without an offset is parsed as the BROWSER's local time.
 *
 * On an IST browser that combination stored a brand-new permit as starting
 * 5.5 hours in the past, and made a "12 hour" window 6.5 hours long. The field
 * also showed UTC to a user whose site runs on IST.
 *
 * These helpers put both ends in the tenant's zone: the string a user reads and
 * edits is their site's wall clock, and the instant sent to the API is the UTC
 * equivalent of it.
 */

/** Fall back to UTC — the same default the backend applies. */
const DEFAULT_TZ = 'UTC';

const partsIn = (date, timeZone) => {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const out = {};
  for (const { type, value } of fmt.formatToParts(date)) {
    if (type !== 'literal') out[type] = value;
  }
  // Intl renders midnight as hour 24 in some engines; normalise it.
  if (out.hour === '24') out.hour = '00';
  return out;
};

/** Is this a zone the runtime actually knows? */
export const isValidTimeZone = (timeZone) => {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
};

const safeZone = (timeZone) => (isValidTimeZone(timeZone) ? timeZone : DEFAULT_TZ);

/**
 * An instant, as the wall clock a tenant user would read.
 * Returns "YYYY-MM-DDTHH:mm", the shape datetime-local expects.
 */
export const toTenantLocalInput = (instant, timeZone) => {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (!date || isNaN(date.getTime())) return '';
  const p = partsIn(date, safeZone(timeZone));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

/** Right now, as the tenant's wall clock. */
export const nowInTenantInput = (timeZone) => toTenantLocalInput(new Date(), timeZone);

/**
 * The reverse: a wall clock the user typed, in the tenant's zone, back to the
 * instant it names.
 *
 * Derived rather than assumed — the zone's offset is measured at that date, so
 * daylight-saving transitions are handled without a table of rules. Returns
 * null for anything that is not a complete datetime-local value, so a caller
 * never silently submits a guess.
 *
 * One case has no lossless answer, in any implementation: the hour a
 * fall-back repeats. "2026-10-25T01:30" happens twice in Europe/London, so a
 * wall clock cannot name which one, and the earlier is chosen. A wall clock
 * inside a spring-forward gap never happens at all, and resolves to the
 * instant just after the jump. Both are conventions, not defects.
 */
/** How far the zone is ahead of UTC at a given instant, in milliseconds. */
const offsetAt = (instant, zone) => {
  const p = partsIn(instant, zone);
  const asUtc = Date.UTC(
    Number(p.year), Number(p.month) - 1, Number(p.day),
    Number(p.hour), Number(p.minute), Number(p.second),
  );
  return asUtc - instant.getTime();
};

export const tenantInputToInstant = (value, timeZone) => {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(String(value || ''))) return null;
  const zone = safeZone(timeZone);
  const wallAsUtc = Date.parse(`${value}:00Z`);
  if (isNaN(wallAsUtc)) return null;

  // Read the wall clock as if it were UTC, measure how far off that lands, and
  // correct. That single pass is wrong across a daylight-saving boundary: the
  // offset is measured on one side of the transition and applied to an instant
  // on the other, which put the answer an hour out (9 of 192 transition-day
  // hours for America/New_York before this). So re-measure the offset AT the
  // candidate instant and correct once more, which settles it.
  const firstOffset = offsetAt(new Date(wallAsUtc), zone);
  let instant = new Date(wallAsUtc - firstOffset);
  const settledOffset = offsetAt(instant, zone);
  if (settledOffset !== firstOffset) {
    instant = new Date(wallAsUtc - settledOffset);
  }
  return instant;
};

/**
 * Whatever a date control hands back, expressed as a wall clock.
 *
 * The shared `DateTimePicker` does NOT emit a bare datetime-local string — it
 * builds a Date from the browser-local components the user picked and emits
 * `toISOString()`, e.g. "2026-09-04T05:09:00.000Z". Feeding that to
 * `tenantInputToInstant` returned null (it only accepts a bare wall clock), the
 * caller's `?.toISOString()` then produced undefined, the key was dropped from
 * the request body, and the API rejected the permit with work_end_date
 * required — the moment a requester touched either date field.
 *
 * The wall clock the user actually chose is the LOCAL reading of that instant,
 * which is what the picker displays. Normalising here keeps one representation
 * in form state: everything downstream — the 12-hour window, the
 * after-the-start comparison, the submit conversion — deals in wall clocks.
 *
 * @param {string|Date} value a wall clock, or any instant a date control emits
 * @returns {string} "YYYY-MM-DDTHH:mm", or '' if the value names no moment
 */
export const toWallClockInput = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  // Already a bare wall clock (no zone): keep it, trimmed to minutes.
  const bare = text.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/);
  if (bare && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(text)) {
    return `${bare[1]}T${bare[2]}:${bare[3]}`;
  }
  const date = value instanceof Date ? value : new Date(text);
  if (!date || isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
       + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Add hours to a tenant wall clock and return the tenant wall clock. */
export const addHoursInTenantInput = (value, hours, timeZone) => {
  const instant = tenantInputToInstant(value, timeZone);
  if (!instant) return '';
  return toTenantLocalInput(
    new Date(instant.getTime() + hours * 60 * 60 * 1000), timeZone);
};
